import React from 'react';
import { Link } from 'react-router-dom';
import styles from '../styles/TaskList.module.css'; // Assuming path
import { formatDate } from '../utils/styleUtils'; // Assuming path and existence of this utility
import { useSocialLanguage } from '../hooks/useSocialLanguage';

// --- Bilingual (AR/EN) display strings ---
const T = {
  ar: {
    unassigned: 'غير مُسنَد',
    thTaskName: 'اسم المهمة',
    thStatus: 'الحالة',
    thAssignees: 'المسند إليهم',
    thEndDate: 'تاريخ التسليم',
    thPriority: 'الأولوية',
    thActions: 'إجراءات',
    dlTaskName: 'اسم المهمة:',
    dlStatus: 'الحالة:',
    dlAssignees: 'المسند إليهم:',
    dlEndDate: 'تاريخ التسليم:',
    dlPriority: 'الأولوية:',
    dlActions: 'إجراءات:',
    unnamedTask: 'مهمة بدون اسم',
    newCommentTitle: 'تعليق جديد لم تره بعد',
    newComment: '💬 تعليق جديد',
    overdue: '⚠ متأخرة',
    unknown: 'غير معروف',
    na: 'غير متاح',
    view: 'عرض',
    noTasks: 'لا توجد مهام مطابقة للفلاتر الحالية',
  },
  en: {
    unassigned: 'Unassigned',
    thTaskName: 'Task Name',
    thStatus: 'Status',
    thAssignees: 'Assignees',
    thEndDate: 'End Date',
    thPriority: 'Priority',
    thActions: 'Actions',
    dlTaskName: 'Task Name:',
    dlStatus: 'Status:',
    dlAssignees: 'Assignees:',
    dlEndDate: 'End Date:',
    dlPriority: 'Priority:',
    dlActions: 'Actions:',
    unnamedTask: 'Unnamed Task',
    newCommentTitle: "New comment you haven't seen yet",
    newComment: '💬 New comment',
    overdue: '⚠ Overdue',
    unknown: 'Unknown',
    na: 'N/A',
    view: 'View',
    noTasks: 'No tasks match the current filters.',
  },
};

// Display-only labels for stored (English) status / priority values.
const statusLabel = (s, lang) =>
  lang === 'ar'
    ? ({
        'Not Started': 'لم تبدأ',
        'In Progress': 'قيد التنفيذ',
        'Company Approval': 'موافقة الشركة',
        'Customer Approval': 'موافقة العميل',
        'Completed': 'مكتملة',
        'Cancelled': 'ملغاة',
      }[s] || s)
    : s;

const priorityLabel = (p, lang) =>
  lang === 'ar'
    ? ({ High: 'عالية', Medium: 'متوسطة', Low: 'منخفضة' }[p] || p)
    : p;

// --- Priority Configuration ---
const priorityOrder = {
    'high': 1,
    'medium': 2,
    'low': 3,
};

// Helper function to get a numerical value for sorting priorities
const getPriorityValue = (priority) => {
    return priorityOrder[priority?.toLowerCase()] || 99; // Default to a low value if undefined/unknown
};

// Helper function for status class
const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
        case 'completed': return styles.statusCompleted;
        case 'company approval':
        case 'customer approval': return styles.statusApproval;
        case 'not started': return styles.statusNotStarted;
        // Add other specific statuses if needed
        default: return styles.statusDefault; // Fallback for 'In Progress' etc.
    }
};

// Helper function for priority class (used for text color in this case)
const getPriorityClass = (priority) => {
    switch (priority?.toLowerCase()) {
        case 'high': return styles.priorityHigh;
        case 'medium': return styles.priorityMedium;
        case 'low': return styles.priorityLow;
        default: return styles.priorityDefault;
    }
};

// Component to render sort indicators (simple text arrows)
const SortIndicator = ({ direction }) => {
    if (!direction) return null;
    // Using text arrows, could be replaced with icons
    return direction === 'asc' ? ' ▲' : ' ▼';
};

// --- Overdue Logic ---
// Get the start of today for comparison
const today = new Date();
today.setHours(0, 0, 0, 0); // Set to midnight local time

// Helper function to check if a task is overdue
const isTaskOverdue = (task) => {
    // Can't be overdue if no end date, or already completed/cancelled
    const statusLower = task.status?.toLowerCase();
    if (!task.endDate || statusLower === 'completed' || statusLower === 'cancelled') {
        return false;
    }
    // Parse end date safely
    let endDate = null;
    if (task.endDate?.toDate) { // Handle Firestore Timestamp
        endDate = task.endDate.toDate();
    } else if (task.endDate instanceof Date) { // Handle JS Date object
        endDate = task.endDate;
    } else if (typeof task.endDate === 'string') { // Handle string date
        try {
            endDate = new Date(task.endDate.includes('T') ? task.endDate : task.endDate + 'T00:00:00Z');
            if (isNaN(endDate.getTime())) endDate = null; // Invalid date string
        } catch (e) {
            endDate = null;
        }
    }

    // Check if valid end date is before the start of today
    return endDate instanceof Date && endDate < today;
};


// Timestamp → millis (handles Firestore Timestamp, {seconds}, or JS Date).
const tsMillis = (v) => {
    if (!v) return 0;
    if (typeof v.toMillis === 'function') return v.toMillis();
    if (typeof v.seconds === 'number') return v.seconds * 1000;
    if (v instanceof Date) return v.getTime();
    return 0;
};

// Does this task have a comment the current user hasn't seen yet?
const hasUnseenComment = (task, uid) => {
    if (!uid || !task.lastCommentAt) return false;
    if (task.lastCommentBy === uid) return false; // their own comment
    const seen = task.commentsSeenAt?.[uid];
    return !seen || tsMillis(seen) < tsMillis(task.lastCommentAt);
};


const TaskList = ({ tasks, employees = [], employeeMap = new Map(), currentUserId, currentSort, onSort }) => {
    const { language } = useSocialLanguage();
    const t = T[language] || T.ar;

    // Helper function to get assignee names as a string
    const getAssigneeNamesString = (assigneeIds) => {
        if (!assigneeIds || assigneeIds.length === 0) {
            return "Unassigned"; // Return simple string (sentinel used for comparison)
        }
        return assigneeIds.map(id => employeeMap.get(id) || `Unknown (${id.slice(0, 5)}...)`).join(', ');
    };

     // Helper function to render assignee names (potentially with styling)
     const renderAssigneeNames = (assigneeIds) => {
        const namesString = getAssigneeNamesString(assigneeIds);
        if (namesString === "Unassigned") {
            return <span className={styles.unassigned}>{t.unassigned}</span>;
        }
        return namesString; // Return the string directly for rendering
    };


    // Function to handle header clicks for sorting
    const handleHeaderClick = (field) => {
        if (onSort) {
            onSort(field);
        }
    };

    // --- Sorting Logic (Memoized) ---
    // Note: This sorting logic primarily affects the order on desktop.
    // The visual display order on mobile is determined by the card structure.
    // However, keeping the data sorted might be useful for other potential operations.
    const sortedTasks = React.useMemo(() => {
        if (!tasks) return [];

        const tasksToSort = [...tasks]; // Create a mutable copy

        // Apply sorting based on DashboardPage's logic passed via currentSort
        // The multi-level sort (Overdue > Priority > User Selection) should ideally
        // happen in the parent (DashboardPage) before passing to TaskList.
        // If TaskList *must* sort independently, replicate the logic here.
        // For simplicity, we'll assume tasks are pre-sorted by DashboardPage
        // based on its `processedTasks` calculation.
        // If independent sorting is needed here, uncomment and adapt the logic below.

        /*
        tasksToSort.sort((a, b) => {
            // --- Replicate sorting logic from DashboardPage if needed ---
            // 1. Overdue status
            const isOverdueA = isTaskOverdue(a);
            const isOverdueB = isTaskOverdue(b);
            if (isOverdueA !== isOverdueB) {
                return isOverdueA ? -1 : 1;
            }

            // 2. Priority
            const priorityA = getPriorityValue(a.priority);
            const priorityB = getPriorityValue(b.priority);
            if (priorityA !== priorityB) {
                return priorityA - priorityB;
            }

            // 3. User selected field (currentSort)
            if (currentSort && currentSort.field) {
                const field = currentSort.field;
                const direction = currentSort.direction === 'asc' ? 1 : -1;
                let valueA = a[field];
                let valueB = b[field];

                // Add specific field handling (dates, assignees etc.) here
                // ...

                // Generic comparison
                if (valueA < valueB) return -1 * direction;
                if (valueA > valueB) return 1 * direction;
            }
            return 0;
        });
        */

        // Assuming tasks are already sorted by the parent (DashboardPage)
        return tasksToSort;

    }, [tasks, currentSort]); // Depend on tasks and currentSort

    // Display message if no tasks are available after filtering/sorting
    if (!sortedTasks || sortedTasks.length === 0) {
        // Use the specific class for styling the "no tasks" message
        return <p className={styles.noTasks}>{t.noTasks}</p>;
    }

    return (
        // The main container for the table/cards
        // overflow-x: auto is handled by CSS for desktop
        <div className={styles.tableContainer}>
            <table className={styles.tasksTable}>
                {/* Table Head - Hidden on mobile via CSS */}
                <thead className={styles.tableHeader}>
                    <tr>
                        {/* Task Name Header */}
                        <th onClick={() => handleHeaderClick('taskName')} className={styles.sortableHeader}>
                            {t.thTaskName}
                            {/* Display sort indicator if this column is being sorted */}
                            {currentSort?.field === 'taskName' && <SortIndicator direction={currentSort.direction} />}
                        </th>
                        {/* Status Header */}
                        <th onClick={() => handleHeaderClick('status')} className={styles.sortableHeader}>
                            {t.thStatus}
                            {currentSort?.field === 'status' && <SortIndicator direction={currentSort.direction} />}
                        </th>
                        {/* Assignees Header */}
                        <th onClick={() => handleHeaderClick('assignees')} className={styles.sortableHeader}>
                            {t.thAssignees}
                            {currentSort?.field === 'assignees' && <SortIndicator direction={currentSort.direction} />}
                        </th>
                        {/* End Date Header */}
                        <th onClick={() => handleHeaderClick('endDate')} className={`${styles.dateColumn} ${styles.sortableHeader}`}>
                            {t.thEndDate}
                            {currentSort?.field === 'endDate' && <SortIndicator direction={currentSort.direction} />}
                        </th>
                        {/* Priority Header - Removed incorrect dateColumn class */}
                        <th onClick={() => handleHeaderClick('priority')} className={`${styles.priorityColumn} ${styles.sortableHeader}`}>
                             {t.thPriority}
                             {currentSort?.field === 'priority' && <SortIndicator direction={currentSort.direction} />}
                         </th>
                         {/* Actions Header */}
                        <th className={styles.actionsColumn}>{t.thActions}</th>
                    </tr>
                </thead>
                {/* Table Body */}
                <tbody className={styles.tableBody}>
                    {sortedTasks.map((task) => {
                        // Determine if the row should have overdue styling
                        const overdue = isTaskOverdue(task);
                        const unseenComment = hasUnseenComment(task, currentUserId);
                        const rowClass = [
                            overdue ? styles.overdueRow : '',
                            unseenComment ? styles.attentionRow : '',
                        ].filter(Boolean).join(' ');
                        const assigneeNames = getAssigneeNamesString(task.assignedTo); // Get simple string for data-label
                        const formattedEndDate = formatDate(task.endDate); // Format date once

                        return (
                            // Apply overdue / attention class if applicable
                            <tr key={task.id} className={rowClass}>
                                {/* Task Name Cell - Add data-label for mobile */}
                                <td data-label={t.dlTaskName}>
                                    <span className={styles.taskNameText}>{task.taskName || t.unnamedTask}</span>
                                    {unseenComment && (
                                        <Link
                                            to={`/task/${task.id}`}
                                            className={styles.newCommentBadge}
                                            title={t.newCommentTitle}
                                        >
                                            <span className={styles.commentDot} /> {t.newComment}
                                        </Link>
                                    )}
                                    {overdue && <span className={styles.overdueBadge}>{t.overdue}</span>}
                                </td>
                                {/* Status Cell - Add data-label for mobile */}
                                <td data-label={t.dlStatus}>
                                    {/* Badge for visual status */}
                                    <span className={`${styles.statusBadge} ${getStatusClass(task.status)}`}>
                                        {task.status ? statusLabel(task.status, language) : t.unknown}
                                    </span>
                                </td>
                                {/* Assignees Cell - Add data-label for mobile */}
                                <td data-label={t.dlAssignees} className={styles.assigneesCell}>
                                    {/* Render styled names */}
                                    {renderAssigneeNames(task.assignedTo)}
                                </td>
                                {/* End Date Cell - Add data-label for mobile */}
                                <td data-label={t.dlEndDate} className={styles.dateColumn}>
                                    {formattedEndDate}
                                </td>
                                {/* Priority Cell - Add data-label for mobile */}
                                <td data-label={t.dlPriority} className={`${styles.priorityColumn} ${getPriorityClass(task.priority)}`}>
                                    {task.priority ? priorityLabel(task.priority, language) : t.na}
                                </td>
                                {/* Actions Cell - Add data-label for mobile */}
                                <td data-label={t.dlActions} className={styles.actionsColumn}>
                                    {/* Link to view task details */}
                                    <Link to={`/task/${task.id}`} className={styles.viewButton}>
                                        {t.view}
                                    </Link>
                                    {/* Add other actions like Edit/Delete here if needed */}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default TaskList;
